<script lang="ts">
  import { CircleHelp } from "lucide-svelte"
  import { xhr, toast } from "$app/utils"
  import { Pulse } from "svelte-loading-spinners"
  import Honeybadger from "$app/utils/honeybadger"
  import PreAlignment from "./PreAlignment.svelte"
  import PostAlignment from "./PostAlignment.svelte"
  import AlignmentTestResponses from "./AlignmentTestResponses.svelte"
  import { appStore } from "$app/store.svelte"

  let props = $props()
  let {
    alignment_test,
    onupdate
  }: {
    alignment_test: AlignmentTestType
    onupdate: (data: AlignmentTestType) => void
  } = props

  let saving = $state(false)
  let errors = {}
  async function sendPreInvitations(alignment_test) {
    errors = {}

    saving = true
    let selected_users = alignment_test.invitable_users.filter(
      (u) => u.selected
    )
    if (selected_users?.length == 0) {
      alert("Please select at least one member to invite")
      return
    }

    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/alignment_tests/${alignment_test.id}/send_pre_invitations`,
        {
          user_ids: selected_users.map((u) => u.id),
          alignment_test: {
            has_time_limit: alignment_test.has_time_limit,
            time_limit: alignment_test.time_limit
          }
        }
      )

      alignment_test = resp.data.alignment_test
      onupdate(alignment_test)

      saving = false
    } catch (e) {
      toast("There was an error updating the alignment test", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      alignment_test = alignment_test
    }
  }

  async function sendPostInvitations(alignment_test) {
    errors = {}
    saving = true

    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/admin/alignment_test/${alignment_test.id}/send_post_invitations`,
        {}
      )

      alignment_test = resp.data.alignment_test
      onupdate(alignment_test)

      saving = false
    } catch (e) {
      toast("There was an error updating the alignment test", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }
</script>

{#if saving}
  <div class="py-12 flex justify-center w-full">
    <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
  </div>
{:else}
  <div class="px-4 py-4 bg-gray-50 border-t border-gray-500">
    {#if alignment_test.pre_test_status === "not_started"}
      <h3 class="font-bold py-2">Alignment Test Settings</h3>

      <div
        class="max-w-sm mx-auto flex justify-between py-1 px-4 hover:bg-indigo-50 mouse-pointer"
      >
        <div class="flex items-center">
          <span class="text-gray-600">Time Limit</span>
        </div>
        <div class="flex items-center">
          <button
            onclick={() =>
              (alignment_test.has_time_limit = !alignment_test.has_time_limit)}
            class="{alignment_test.has_time_limit
              ? 'bg-green-400'
              : 'bg-gray-200'}  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
            aria-label="Time Limit"
          >
            <span
              aria-hidden="true"
              class="{alignment_test.has_time_limit
                ? 'translate-x-5'
                : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform bg-white rounded-full shadow ring-0 transition duration-200 ease-in-out"
            ></span>
          </button>
        </div>
      </div>
      {#if alignment_test.has_time_limit}
        <div class="max-w-sm mx-auto flex justify-between py-1 px-4">
          <div class="flex items-center">
            <span class="text-gray-600">Time Limit Minutes</span>
          </div>
          <div class="flex items-center">
            <select
              bind:value={alignment_test.time_limit}
              class="block min-w-32 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
            >
              <option value={5}>5 Minutes</option>
              <option value={8}>8 Minutes</option>
              <option value={10}>10 Minutes</option>
              <option value={15}>15 Minutes</option>
            </select>
          </div>
        </div>
      {/if}
      <h3 class="font-bold py-2 mt-6">
        Select Members to Include in this Alignment Test:
      </h3>

      <ul>
        {#each alignment_test.invitable_users as user (user.id)}
          <li
            class="max-w-sm mx-auto flex justify-between py-1 px-4 hover:bg-indigo-50 mouse-pointer"
          >
            <div class="flex items-center">
              <img
                src={user.avatar_thumb_url}
                class="w-8 h-8 rounded-lg mr-2"
                alt={user.name}
              />
              <span class="text-gray-600">{user.name}</span>
              {#if user.role === "alignment"}
                <div
                  class="ml-2 inline-flex items-center rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20"
                >
                  <span class="font-sm font-bold">Alignment-Only</span>
                </div>
              {/if}
            </div>
            <button
              type="button"
              aria-label="Select User"
              onclick={() => (user.selected = !user.selected)}
              class="{user.selected
                ? 'bg-green-400'
                : 'bg-gray-200'}  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
            >
              <span
                aria-hidden="true"
                class="{user.selected
                  ? 'translate-x-5'
                  : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform bg-white rounded-full shadow ring-0 transition duration-200 ease-in-out"
              ></span>
            </button>
          </li>
        {/each}
      </ul>

      <div class="mt-4 py-4 flex justify-center">
        <button
          type="button"
          onclick={() => sendPreInvitations(alignment_test)}
          class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
          >Send Pre-Alignment Test Invitations</button
        >
      </div>

      <div class="border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <CircleHelp class="w-8 h-8 text-yellow-500 " />
          </div>
          <div class="ml-5">
            <p class="text-sm text-yellow-700">
              Don't see a member that should participate in the alignment test?
              Click the <span class="font-bold">Participants</span> tab above to
              add additional members.
            </p>
          </div>
        </div>
      </div>
    {:else if alignment_test.pre_test_status === "collecting_pre_alignment_responses"}
      <div class="flex justify-start items-center">
        <h3 class="text-xl md:text-2xl font-bold py-2 mt-6">
          Pre-Alignment Test Participant Status
        </h3>
      </div>
      <AlignmentTestResponses
        responses={alignment_test.alignment_test_responses}
        type="pre"
      />
    {:else if alignment_test.pre_test_status === "scoring_pre_test_responses"}
      <h3 class="text-xl md:text-2xl font-bold py-2 mt-4">
        Pre-Alignment Test Scoring
      </h3>
      <div class="rounded-md bg-blue-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-blue-700">
              Scoring of the Pre-Alignment Test currently in progress. You will
              receive an email once it is completed.
            </p>
          </div>
        </div>
      </div>
    {:else if alignment_test.pre_test_status === "pre_scoring_completed"}
      <PreAlignment {alignment_test} />
    {/if}

    {#if alignment_test.post_test_status === "invite_post_alignment_responses"}
      <div class="rounded-md bg-yellow-50 p-6 mt-8">
        <h3
          class="text-lg md:text-xl lg:text-3xl font-medium text-orange-800 flex items-center"
        >
          What's Next <div class="flex-shrink-0">
            <CircleHelp class="ml-2 h-8 w-8 text-orange-500" />
          </div>
        </h3>
        <div class="mt-6 text-base text-orange-700">
          <p>
            Now that you have completed your pre-alignment test and have the
            full results available, you can reach out to the Lead With Purpose
            and we would be happy to help you understand your results and
            strategize on how to increase your team's alignment.
          </p>
          <p class="mt-6">
            If you have not yet built your Fast Attack One-Page Captain's Plan,
            now is the time to do so. Once you have your Fast Attack Plan you
            need to ensure it is fully rolled out to your entire team. Once that
            is done, you can then proceed with your Post-Alignment Test to see
            how the Lead With Purpose leadership system has increased the
            alignment of your team.
          </p>
          <p class="mt-6">
            Please do not proceed with the Post-Alignment Test until the One
            Page Captain's Plan is fully completed and rolled out to your entire
            team.
          </p>
        </div>
      </div>

      <div class="bg-gray-100 p-4 rounded mt-6">
        <h3 class="text-xl md:text-3xl font-bold py-2 mt-4">
          Your Post-Alignment Test
        </h3>
        <h3 class="font-bold py-2">
          When you are ready to send out the Post-Alignment Test, please click
          the button below. The following members and participants will be sent
          their invitation to the Post-Alignment Test:
        </h3>

        <ul
          class="grid grid-cols-3 max-w-2xl mx-auto mt-6 gap-6 bg-white rounded p-6"
        >
          {#each alignment_test.alignment_test_users as u (u.id)}
            <li
              class="flex justify-between py-1 px-4 bg-amber-100 border-amber-200 shadow rounded-lg"
            >
              <div class="flex items-center">
                <img
                  src={u.user.avatar_thumb_url}
                  alt={u.user.name}
                  class="w-8 h-8 rounded-lg mr-2"
                />
                <span class="text-gray-600">{u.user.name}</span>
                {#if u.user.role === "alignment"}
                  <div
                    class="ml-2 inline-flex items-center rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20"
                  >
                    <span class="font-sm font-bold">Alignment-Only</span>
                  </div>
                {/if}
              </div>
            </li>
          {/each}
        </ul>

        <div class="mt-6 py-4 flex justify-center">
          <button
            type="button"
            onclick={() => sendPostInvitations(alignment_test)}
            class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            >Send Post-Alignment Test Invitations</button
          >
        </div>
      </div>
    {:else if alignment_test.post_test_status === "collecting_post_alignment_responses"}
      <h3 class="text-xl md:text-2xl font-bold py-2 mt-4">
        Post-Alignment Test Participant Status
      </h3>
      <AlignmentTestResponses
        responses={alignment_test.alignment_test_responses}
        type="post"
      />
    {:else if alignment_test.post_test_status === "scoring_post_test_responses"}
      <h3 class="text-xl md:text-2xl font-bold py-2 mt-4">
        Post-Alignment Test Scoring
      </h3>
      <div class="rounded-md bg-blue-50 p-4 mt-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-blue-700">
              Scoring of the Post-Alignment Test currently in progress. You will
              receive an email once it is completed.
            </p>
          </div>
        </div>
      </div>
    {:else if alignment_test.post_test_status === "post_scoring_completed"}
      <PostAlignment {alignment_test} />
    {/if}
  </div>
{/if}

<style global>
  @media print {
    .pe-no-print {
      display: none !important;
    }

    .pe-preserve-ancestor {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
    }
  }
</style>
